import { MediaItem } from '@/Shared/Types';
import {  useState } from 'react';


export interface LightBoxData {
    medias?: MediaItem[], 
    initialIndex: number,
}

export interface LightBoxHook {
    closeLightBox: () => void, 
    lightBoxData : LightBoxData|null;
    openLightBox: (data: LightBoxData) => void,
}

/**
 * =====================================================
 * Hook: useLightBox
 * =====================================================
 *
 * Description:
 * Custom hook to manage LightBox state using the Command pattern.
 * Provides an external API to open and close a LightBox and
 * maintain the currently displayed media and index.
 *
 * Responsibilities:
 * - Track currently active LightBox data (`media items` + `initial index`).
 * - Provide functions to open or close the LightBox.
 * - Keep state fully controlled by the parent/consumer.
 *
 * Notes:
 * - Stateless with respect to media behavior; only stores display data.
 * - Designed to work seamlessly with the LightBox component.
 *
 * Author: Samano CASTRE
 * Date: February 4, 2026
 */
export function useLightBox() : LightBoxHook {

     /** =====================================================
     * Local State
     * =====================================================
     *
     * Holds the LightBox data or null if closed.
     * Structure:
     * - medias: array of MediaItem (image or video)
     * - initialIndex: index of the media to start with
     */
    const [lightBoxData, setLightBoxData] = useState<LightBoxData | null>(null);

     /** =====================================================
     * Command Functions
     * =====================================================
     *
     * Expose a controlled API for opening and closing the LightBox.
     * - openLightBox: sets the LightBox data to display
     * - closeLightBox: clears the LightBox data (closes LightBox)
     */
    const openLightBox = (data: LightBoxData) => {
        setLightBoxData(data);
    }
    const closeLightBox = () => {
        setLightBoxData(null);
    };

     /** =====================================================
     * Return API
     * =====================================================
     *
     * Returns the current LightBox state and command functions
     * for external control by components.
     */
    return {
        lightBoxData,
        openLightBox,
        closeLightBox
    };
}
